import { Instant, IsoFields, ZonedDateTime, ZoneId } from "@js-joda/core";
import { DatasetDataInfoResponse } from "api/model/dataset/DatasetDataInfoResponse";
import { InstantDoubleValue } from "api/model/dataset/InstantDoubleValue";
import { T } from "services/lang/LanguageService";
import {
  DATE_FORMATTER_DAYS,
  DATE_FORMATTER_HOURS_MINUTES_ONLY,
  DATE_FORMATTER_HOURS_ONLY,
  DATE_FORMATTER_MINUTES,
  DATE_FORMATTER_MONTH_DAY_ONLY,
} from "utils/constants";
import { getMonthRangeIn445Calendar } from "utils/four-four-five-calendar-utils";
import { toInstant, toZonedDateTime, toZonedDateTimeOrNow } from "utils/time-utils";
import { AggregationPeriod, DataDisplayType, DiffType } from "./ValueChangeIndicatorTypes";

export const getMainClassName = (
  dataDisplayType: DataDisplayType,
  reportMode?: boolean,
  displayInline?: boolean
) => {
  const currentClassName = `last-value-indicator${reportMode ? "-report-mode" : ""}`;

  if (dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART) {
    return `${currentClassName}-horizontal`;
  }
  if (displayInline) {
    return `${currentClassName}-inline`;
  }
  return currentClassName;
};

export const getTitleClassName = (dataDisplayType: DataDisplayType, displayInline?: boolean): string => {
  const baseTitle = dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ? "title-horizontal" : "title";

  const fontWeight =
    displayInline || dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART ? "font-bold" : "font-medium";

  return `${baseTitle} ${fontWeight}`;
};

export const getDiffClassName = (
  isNeutral: boolean,
  dataDisplayType: DataDisplayType,
  useDiffIndicatorColors?: boolean
): string => {
  const baseDiff =
    dataDisplayType === DataDisplayType.HORIZONTAL_BAR_CHART
      ? "last-value-diff-horizontal"
      : "last-value-diff";

  const classNames = [
    baseDiff,
    "flex flex-row align-items-center justify-content-end",
    useDiffIndicatorColors && !isNeutral && "indicator-colors",
    isNeutral && "neutral",
  ]
    .filter(Boolean)
    .join(" ");

  return classNames;
};

export const getPeriodLabelIn445Calendar = (
  unit: AggregationPeriod,
  referenceTime: Instant | undefined,
  zoneId: ZoneId,
  isStartOfThePeriod?: boolean
): string => {
  const ref = toZonedDateTimeOrNow(zoneId, referenceTime);

  switch (unit) {
    case AggregationPeriod.MINUTES:
      return ref.format(DATE_FORMATTER_HOURS_MINUTES_ONLY);
    case AggregationPeriod.HOURS:
      return ref.format(DATE_FORMATTER_HOURS_ONLY);
    case AggregationPeriod.DAYS:
      return ref.format(DATE_FORMATTER_DAYS);
    case AggregationPeriod.WEEKS: {
      const week = ref.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
      return `W${week}`;
    }
    case AggregationPeriod.MONTHS: {
      const [startOfMonth, endOfMonth] = getMonthRangeIn445Calendar(ref.toInstant(), zoneId);

      let targetDate = endOfMonth;
      if (isStartOfThePeriod) {
        targetDate = startOfMonth;
      }

      return targetDate.format(DATE_FORMATTER_MONTH_DAY_ONLY);
    }
    case AggregationPeriod.YEARS:
      return String(ref.year());
    default:
      return ref.format(DATE_FORMATTER_MINUTES);
  }
};

export const getPeriodLabel = (
  Unit: AggregationPeriod,
  referenceTime: Instant | undefined,
  zoneId: ZoneId
) => {
  const currentAlignUnit = (Unit ?? AggregationPeriod.MONTHS).toString().toUpperCase();
  const ref = toZonedDateTime(referenceTime || Instant.now(), zoneId);

  switch (currentAlignUnit) {
    case AggregationPeriod.MINUTES:
      return ref.format(DATE_FORMATTER_HOURS_MINUTES_ONLY);
    case AggregationPeriod.HOURS:
      return ref.format(DATE_FORMATTER_HOURS_ONLY);
    case AggregationPeriod.DAYS:
      return ref.format(DATE_FORMATTER_DAYS);
    case AggregationPeriod.WEEKS: {
      const week = ref.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
      return `W${week}`;
    }
    case AggregationPeriod.MONTHS: {
      const lastDayOfMonth = ref.withDayOfMonth(1).minusDays(1);
      return lastDayOfMonth.format(DATE_FORMATTER_MONTH_DAY_ONLY);
    }
    case AggregationPeriod.YEARS:
      return String(ref.year());
    default:
      return ref.format(DATE_FORMATTER_MINUTES);
  }
};

export const getMonthYearComparison = (dataset: InstantDoubleValue[], zone: ZoneId, refTime?: Instant) => {
  const ref = toZonedDateTimeOrNow(zone, refTime);

  const firstDayOfCurrentMonth = ref.withDayOfMonth(1).withHour(0).withMinute(0).withSecond(0).withNano(0);

  const targetYear = firstDayOfCurrentMonth.year();
  const targetMonth = firstDayOfCurrentMonth.monthValue();

  const findValue = (y: number, m: number) => {
    return (
      dataset.find(d => {
        const dt = ZonedDateTime.parse(d.key).withZoneSameInstant(zone);
        return dt.year() === y && dt.monthValue() === m;
      })?.value ?? null
    );
  };

  const curr = findValue(targetYear, targetMonth);
  const prev = findValue(targetYear - 1, targetMonth);
  const currValueKey = `${targetYear}-${targetMonth}`;
  const prevValueKey = `${targetYear - 1}-${targetMonth}`;

  return { curr, prev, currValueKey, prevValueKey };
};

export const getNoDataMessageWeek = (upToLastFinishedWeek?: boolean) =>
  upToLastFinishedWeek ? T("label.no-data-last-finished-week") : T("label.no-data-current-week");

export const getDiffClass = (isNegative: boolean) => (isNegative ? "negative" : "positive");

export const getArrowDirection = (isNegative: boolean) => (isNegative ? "down" : "up");

export const formatDiff = (
  prev: number,
  curr: number,
  unit?: string,
  value?: number,
  isNeutral?: boolean,
  diffType?: DiffType,
  decimalPlaces?: number,
  dataDisplayType?: DataDisplayType
) => {
  if (!value) return "0";
  if (dataDisplayType !== DataDisplayType.HORIZONTAL_BAR_CHART) {
    if (isNeutral) {
      return "0";
    }

    if (diffType === DiffType.PERCENTAGE) {
      const percent = ((curr - prev) / prev) * 100;
      return `${percent.toFixed(decimalPlaces)}%`;
    }
    return `${(curr - prev).toFixed(decimalPlaces)} ${unit}`;
  }
  return `${value.toFixed(decimalPlaces)} ${diffType === DiffType.PERCENTAGE ? "%" : unit}`;
};

export const getFromDate = (startOfTheProject: Instant, tz: ZoneId): string => {
  const currentStartOfTheProject = toZonedDateTime(startOfTheProject, tz);

  return currentStartOfTheProject.format(DATE_FORMATTER_MONTH_DAY_ONLY);
};

export const calculateFromStartOfTheProject = (
  tz: ZoneId,
  refTime: Instant,
  defaultFromDate: Instant,
  startOfTheProject?: Instant,
  useStartOfTheProject?: boolean
): boolean => {
  if (!startOfTheProject) return false;

  const startInstant = toInstant(startOfTheProject);
  const projectStart = startInstant.atZone(tz).toLocalDate();

  const defaultFromInstant = toInstant(defaultFromDate);
  const defaultFrom = defaultFromInstant.atZone(tz).toLocalDate();

  const todayLocal = toZonedDateTimeOrNow(tz, refTime).toLocalDate();

  return (
    !!useStartOfTheProject &&
    projectStart.isAfter(defaultFrom) &&
    (projectStart.isBefore(todayLocal) || projectStart.equals(todayLocal))
  );
};

const getReferenceWeek = (tz: ZoneId, refTime?: Instant) => {
  const refZdt = toZonedDateTimeOrNow(tz, refTime);
  return refZdt.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
};

export const getWeekYearComparison = (dataset: DatasetDataInfoResponse, tz: ZoneId, refTime?: Instant) => {
  if (!dataset?.data) return null;

  const zdtRef = toZonedDateTimeOrNow(tz, refTime);
  const refWeek = getReferenceWeek(tz, refTime);
  const refMonth = zdtRef.monthValue();
  const refYear = refWeek === 1 && refMonth === 12 ? zdtRef.year() + 1 : zdtRef.year();

  const groupedByYear = new Map<number, number>();

  dataset.data.forEach(point => {
    const zdt = toZonedDateTime(point.key, tz);
    const week = zdt.get(IsoFields.WEEK_OF_WEEK_BASED_YEAR);
    const month = zdt.monthValue();
    const year = week === 1 && month === 12 ? zdt.year() + 1 : zdt.year();

    if (week === refWeek && groupedByYear.get(year) === undefined) {
      groupedByYear.set(year, point.value ?? 0);
    }
  });

  const current = groupedByYear.get(refYear);
  const comparisons = Array.from(groupedByYear.entries())
    .filter(([year]) => year !== refYear)
    .map(([year, value]) => ({ year, value }));
  return { current, comparisons, refWeek, refYear };
};
